import { useCallback, useEffect, useMemo, useState } from "react";
import { api, errMsg, itDate, STATUS_META, TYPE_LABELS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { VacationDialog } from "@/components/VacationDialog";
import { toast } from "sonner";
import { Plus, Check, X, Umbrella, Warning, Trash, Archive } from "@phosphor-icons/react";



// causale proposta nel registro giornaliero in base al tipo di richiesta
const DEFAULT_STATUS = {
  FERIE: "FERIE", RIPOSO: "RIPOSO", PERMESSO: "PERMESSO",
  PERMESSO_104: "PERMESSO", MALATTIA: "MALATTIA", INFORTUNIO: "INFORTUNIO",
};

const STATUS_BADGE = {  IN_ATTESA: "bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-300",
  APPROVATA: "bg-emerald-100 text-emerald-900 dark:bg-emerald-950 dark:text-emerald-300",
  RIFIUTATA: "bg-rose-100 text-rose-900 dark:bg-rose-950 dark:text-rose-300",
};

const dateTimeIt = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return `${itDate(iso.slice(0, 10))} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

export default function Requests() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canDecide = ["admin", "manager", "titolare"].includes(user?.role);
  const isEmployee = user?.role === "employee";
  const canRemove = ["admin", "manager"].includes(user?.role) || isEmployee;
  const canDeleteRow = (r) => canRemove && (r.status !== "APPROVATA" || !isEmployee);
  const canRejectRow = (r) => canDecide && ["IN_ATTESA", "APPROVATA"].includes(r.status);
  const isOwner = user?.role === "titolare";
  const canSeeVacations = ["admin", "manager", "titolare"].includes(user?.role);
  const [tab, setTab] = useState("richieste");
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("IN_ATTESA");
  const [type, setType] = useState("all");
  const [form, setForm] = useState(null);
  const [reject, setReject] = useState(null);
  const [confirmApprove, setConfirmApprove] = useState(null);
  const [approve, setApprove] = useState(null);
  const [vacationOf, setVacationOf] = useState(null);
  const [dettaglio, setDettaglio] = useState(null);
  const [q, setQ] = useState("");
  const [cerca, setCerca] = useState("");
  const [archivio, setArchivio] = useState(false);
  const [employees, setEmployees] = useState([]);
  const filteredEmployees = useMemo(() => employees.filter(
    (e) => `${e.last_name} ${e.first_name} ${e.code}`.toLowerCase().includes(q.toLowerCase())),
    [employees, q]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!isEmployee) {
      api.get("/employees", { params: { status: "active" } }).then((r) => setEmployees(r.data)).catch(() => {});
    }
  }, [isEmployee]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/requests", { params: { status, type } });
      setRows(data);
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  }, [status, type]);

  useEffect(() => { load(); }, [load]);

  const GIORNI_ARCHIVIO = 45;
  const vecchia = (r) => {
    if (!r.decided_at) return false;
    return (Date.now() - new Date(r.decided_at).getTime()) / 86400000 > GIORNI_ARCHIVIO;
  };
  const storiche = rows.filter(vecchia);
  const testo = cerca.trim().toLowerCase();
  const perNome = (r) => !testo || (r.employee_name || "").toLowerCase().includes(testo);
  const visibili = (archivio ? rows : rows.filter((r) => !vecchia(r))).filter(perNome);

  const decide = async (r, action, reason = null, status = null) => {
    try {
      await api.patch(`/requests/${r.id}`, { action, reason: reason || null, status: status || null });
      toast.success(action === "approve"
        ? `Richiesta approvata e registrata come ${status || ""} nel calendario`.replace("  ", " ")
        : "Richiesta rifiutata");
      setReject(null);
      setConfirmApprove(null);
      setApprove(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const removeRequest = async (r) => {
    let avviso = `Eliminare la richiesta di ${r.employee_name}?`;
    if (r.status === "APPROVATA") {
      try {
        const { data } = await api.delete(`/requests/${r.id}`, { params: { dry_run: true } });
        avviso = data.attendance
          ? `Eliminare la richiesta di ${r.employee_name} e le ${data.attendance} causali ${data.status} dal foglio presenze?`
          : `Eliminare la richiesta di ${r.employee_name}? Nessuna causale da rimuovere dal foglio presenze.`;
      } catch (e) { toast.error(errMsg(e)); return; }
    }
    if (!window.confirm(avviso)) return;
    try {
      const { data } = await api.delete(`/requests/${r.id}`);
      toast.success(data.attendance_removed
        ? `Richiesta eliminata e ${data.attendance_removed} causali rimosse dalle presenze`
        : "Richiesta eliminata");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const pulisciDuplicati = async () => {
    try {
      const { data } = await api.post("/requests/pulizia-duplicati");
      toast.success(data.duplicati
        ? `${data.rimossi} richieste doppie rimosse`
        : "Nessuna richiesta doppia trovata");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const submit = async () => {
    if (sending) return;
    setSending(true);
    try {
      await api.post("/requests", {
        employee_id: form.employee_id || null, type: form.type,
        date_from: form.date_from, date_to: form.type === "PERMESSO" ? form.date_from : (form.date_to || form.date_from),
        notes: form.notes || null,
      });
      toast.success("Richiesta inviata");
      setForm(null);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setSending(false); }
  };

  return (
    <div data-testid="requests-page" className="space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-head font-bold tracking-tight">Richieste</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Ferie, riposi, permessi e malattie · storico decisioni
          </p>
        </div>
        {!isOwner && (
          <div className="flex flex-wrap gap-2">
            {isAdmin && tab === "richieste" && (
              <Button size="sm" variant="outline" className="rounded-sm font-semibold"
                data-testid="clean-duplicates-btn" onClick={pulisciDuplicati}>
                Pulisci duplicati
              </Button>
            )}
            <Button size="sm" className="rounded-sm font-semibold" data-testid="add-request-btn"
              onClick={() => setForm({ type: "FERIE", employee_id: "", date_from: "", date_to: "", notes: "" })}>
              <Plus size={15} className="mr-1.5" /> Nuova richiesta
            </Button>
          </div>
        )}
      </div>

      {canSeeVacations && (
        <div className="flex gap-1 border-b border-border">
          {[["richieste", "Richieste e storico"], ["ferie", "Situazione ferie"]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
              className={`px-3 py-2 text-sm font-semibold transition-colors duration-150 border-b-2 -mb-px
                ${tab === k ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
              {l}
            </button>
          ))}
        </div>
      )}

      {tab === "ferie" && canSeeVacations && (
        <div className="space-y-3" data-testid="vacations-section">
          <div className="rounded-sm border border-border bg-card p-3">
            <Input data-testid="vacation-search" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Cerca dipendente..." className="h-9 rounded-sm max-w-xs" />
          </div>
          <div className="rounded-sm border border-border bg-card overflow-x-auto scroll-thin">
            <table className="w-full text-sm tbl-sticky" data-testid="vacations-table">
              <thead className="bg-secondary">
                <tr>{["", "Dipendente", "Matricola", "Reparto"].map((h) => (
                  <th key={h} className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap">{h}</th>
                ))}</tr>
              </thead>
              <tbody>
                {filteredEmployees.map((e) => (
                  <tr key={e.id} className="border-t border-border hover:bg-secondary/40"
                    data-testid={`vacation-emp-${e.id}`}>
                    <td className="px-3 py-1.5 text-left">
                      <Button size="sm" variant="outline" className="h-8 rounded-sm"
                        data-testid={`open-vacation-${e.id}`}
                        onClick={() => setVacationOf({ id: e.id, name: `${e.last_name} ${e.first_name}` })}>
                        <Umbrella size={14} className="mr-1.5" /> Ferie
                      </Button>
                    </td>
                    <td className="px-3 py-1.5 font-semibold">{e.last_name} {e.first_name}</td>
                    <td className="px-3 py-1.5 tabular-nums text-muted-foreground">{e.code}</td>
                    <td className="px-3 py-1.5 text-muted-foreground">{e.department || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "richieste" && (
      <>
      <div className="rounded-sm border border-border bg-card p-3 flex flex-wrap gap-2">
        <Input value={cerca} onChange={(e) => setCerca(e.target.value)}
          placeholder="Cerca dipendente per nome o cognome..."
          className="h-9 w-[260px] rounded-sm" data-testid="request-search" />
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="h-9 w-[170px] rounded-sm" data-testid="request-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli stati</SelectItem>
            <SelectItem value="IN_ATTESA">In attesa</SelectItem>
            <SelectItem value="APPROVATA">Approvate</SelectItem>
            <SelectItem value="RIFIUTATA">Rifiutate</SelectItem>
          </SelectContent>
        </Select>
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="h-9 w-[210px] rounded-sm" data-testid="request-type-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i tipi</SelectItem>
            {Object.entries(TYPE_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant={archivio ? "default" : "outline"} size="sm"
          className="h-9 rounded-sm font-semibold" data-testid="request-archive-toggle"
          onClick={() => setArchivio(!archivio)}
          title={`Richieste decise da più di ${GIORNI_ARCHIVIO} giorni`}>
          <Archive size={15} className="mr-1.5" />
          {archivio ? "Nascondi archivio" : `Mostra archivio (${storiche.length})`}
        </Button>
      </div>

      <div className="rounded-sm border border-border bg-card overflow-x-auto scroll-thin">
        <table className="w-full text-sm tbl-sticky" data-testid="requests-table">
          <thead className="bg-secondary">
            <tr>{[["Azioni", 220], ["Dipendente", 190], ["Tipo", 110], ["Dal", 96], ["Al", 96], ["Giorni", 70],
                  ["Note", 180], ["Stato", 120], ["Decisa da", 140], ["Data decisione", 130],
                  ["Motivazione", 180]].map(([h, w]) => (
              <th key={h} style={{ width: w }}
                className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap">{h}</th>
            ))}</tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={11} className="px-3 py-8 text-center text-muted-foreground">Caricamento...</td></tr>}
            {!loading && visibili.length === 0 && <tr><td colSpan={11} className="px-3 py-8 text-center text-muted-foreground" data-testid="requests-empty">
              {rows.length > 0 ? (testo ? `Nessuna richiesta per "${cerca}"`
                : `Nessuna richiesta recente: ${storiche.length} in archivio (oltre ${GIORNI_ARCHIVIO} giorni)`)
                : "Nessuna richiesta"}
            </td></tr>}
            {visibili.map((r) => (
              <tr key={r.id} className="border-t border-border hover:bg-secondary/40 cursor-pointer"
                data-testid={`request-row-${r.id}`} onClick={() => setDettaglio(r)}>
                <td className="px-3 py-1.5" onClick={(e) => e.stopPropagation()}>
                  <div className="flex flex-nowrap items-center gap-1.5">
                    {canDecide && r.status === "IN_ATTESA" && (
                      <>
                        <Button size="sm" className="h-8 rounded-sm font-semibold px-2.5 whitespace-nowrap" data-testid={`approve-${r.id}`}
                          onClick={() => setApprove({ r, status: DEFAULT_STATUS[r.type] || "FERIE" })}>
                          <Check size={14} className="mr-1" /> Approva
                        </Button>
                      </>
                    )}
                    {canRejectRow(r) && (
                      <Button size="sm" variant="outline"
                        className="h-8 rounded-sm px-2.5 font-semibold whitespace-nowrap border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground"
                        onClick={() => setReject({ r, reason: "" })} data-testid={`reject-${r.id}`}>
                        <X size={14} className="mr-1" /> Rifiuta
                      </Button>
                    )}
                    {canDeleteRow(r) && (
                      <button type="button" title="Elimina richiesta" data-testid={`delete-request-${r.id}`}
                        className="rounded-sm p-1.5 text-rose-600 hover:text-rose-700" onClick={() => removeRequest(r)}>
                        <Trash size={16} />
                      </button>
                    )}
                  </div>
                </td>
                <td className="px-3 py-1.5">
                  <div className="font-semibold">{r.employee_name}</div>
                  <div className="text-[10px] text-muted-foreground tabular-nums">{r.employee_code} · {r.department}</div>
                </td>
                <td className="px-3 py-1.5 whitespace-nowrap">{TYPE_LABELS[r.type]}</td>
                <td className="px-3 py-1.5 tabular-nums">{itDate(r.date_from)}</td>
                <td className="px-3 py-1.5 tabular-nums">{itDate(r.date_to)}</td>
                <td className="px-3 py-1.5 tabular-nums">{r.days}</td>
                <td className="px-3 py-1.5 text-xs text-muted-foreground max-w-[220px] truncate">{r.notes || "—"}</td>
                <td className="px-3 py-1.5">
                  <span className={`px-1.5 py-0.5 rounded-sm text-[10px] font-bold ${STATUS_BADGE[r.status]}`}>{r.status.replace("_", " ")}</span>
                  {r.overlaps?.length > 0 && (
                    <button onClick={(e) => { e.stopPropagation(); setApprove({ r, status: DEFAULT_STATUS[r.type] || "FERIE" }); }} data-testid={`overlap-badge-${r.id}`}
                      title={`Sovrapposizione con: ${r.overlaps.map((o) => o.employee_name).join(", ")}`}
                      className="ml-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[10px] font-bold bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-300">
                      <Warning size={11} /> {r.overlaps.length}
                    </button>
                  )}
                </td>
                <td className="px-3 py-1.5 text-xs" data-testid={`decided-by-${r.id}`}>
                  {r.decided_by_name || r.decided_by || "—"}
                </td>
                <td className="px-3 py-1.5 text-xs tabular-nums text-muted-foreground">
                  {dateTimeIt(r.decided_at)}
                </td>
                <td className="px-3 py-1.5 text-xs text-muted-foreground max-w-[220px] truncate"
                  title={r.decision_reason || ""}>
                  {r.decision_reason || "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </>
      )}

      {dettaglio && (
        <Dialog open onOpenChange={() => setDettaglio(null)}>
          <DialogContent className="rounded-sm sm:max-w-lg" data-testid="request-detail-dialog">
            <DialogHeader>
              <DialogTitle className="font-head">Dettaglio richiesta</DialogTitle>
            </DialogHeader>
            <div className="divide-y divide-border text-sm">
              {[
                ["Dipendente", dettaglio.employee_name],
                ["Matricola", dettaglio.employee_code],
                ["Reparto", dettaglio.department],
                ["Tipo", TYPE_LABELS[dettaglio.type] || dettaglio.type],
                ["Dal", itDate(dettaglio.date_from)],
                ["Al", itDate(dettaglio.date_to)],
                ["Giorni", dettaglio.days],
                ["Note", dettaglio.notes],
                ["Stato", dettaglio.status?.replace("_", " ")],
                ["Richiesta inviata il", dateTimeIt(dettaglio.created_at)],
                ["Inviata da", dettaglio.created_by_name || dettaglio.created_by],
                ["Decisa da", dettaglio.decided_by_name || dettaglio.decided_by],
                ["Data decisione", dettaglio.decided_at ? dateTimeIt(dettaglio.decided_at) : "—"],
                ["Motivazione", dettaglio.decision_reason],
                ["Causale registrata", dettaglio.applied_status],
                ["Giorni registrati", dettaglio.applied_days],
                ["Sovrapposizioni", dettaglio.overlaps?.length
                  ? dettaglio.overlaps.map((o) => `${o.employee_name} (${o.days} gg)`).join(", ") : ""],
              ].map(([k, v]) => (
                <div key={k} className="flex gap-3 py-1.5" data-testid={`request-detail-${k.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  <span className="w-44 shrink-0 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{k}</span>
                  <span className="min-w-0 break-words">{v === 0 || v ? v : "—"}</span>
                </div>
              ))}
            </div>
            <DialogFooter>
              <Button variant="outline" className="rounded-sm" data-testid="request-detail-close"
                onClick={() => setDettaglio(null)}>Chiudi</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {form && (
        <Dialog open onOpenChange={() => setForm(null)}>
          <DialogContent className="rounded-sm sm:max-w-md" data-testid="request-dialog">
            <DialogHeader><DialogTitle className="font-head">Nuova richiesta</DialogTitle></DialogHeader>
            <div className="space-y-3">
              {!isEmployee && (
                <div>
                  <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Dipendente</Label>
                  <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                    <SelectTrigger className="h-9 rounded-sm mt-1" data-testid="request-employee"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                    <SelectContent className="max-h-72">
                      {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.last_name} {e.first_name} ({e.code})</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Tipo</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger className="h-9 rounded-sm mt-1" data-testid="request-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(TYPE_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Dal</Label>
                  <Input type="date" value={form.date_from} onChange={(e) => setForm({ ...form, date_from: e.target.value })}
                    className="h-9 rounded-sm mt-1" data-testid="request-from" />
                </div>
                <div>
                  <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Al</Label>
                  <Input type="date" value={form.type === "PERMESSO" ? form.date_from : form.date_to}
                    onChange={(e) => setForm({ ...form, date_to: e.target.value })}
                    className="h-9 rounded-sm mt-1" data-testid="request-to" disabled={form.type === "PERMESSO"} />
                </div>
              </div>
              <div>
                <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Note</Label>
                <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="h-9 rounded-sm mt-1" data-testid="request-notes" placeholder="Motivazione (opzionale)" />
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" className="rounded-sm" onClick={() => setForm(null)} data-testid="request-cancel">Annulla</Button>
              <Button className="rounded-sm font-semibold" onClick={submit} disabled={sending} data-testid="request-save">
                {sending ? "Invio..." : "Invia richiesta"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {reject && (
        <Dialog open onOpenChange={() => setReject(null)}>
          <DialogContent className="rounded-sm sm:max-w-md" data-testid="reject-dialog">
            <DialogHeader>
              <DialogTitle className="font-head">Rifiuta richiesta</DialogTitle>
            </DialogHeader>
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                {TYPE_LABELS[reject.r.type]} di <span className="font-semibold text-foreground">{reject.r.employee_name}</span>
                {" "}dal {itDate(reject.r.date_from)} al {itDate(reject.r.date_to)}.
              </p>
              {reject.r.status === "APPROVATA" && (
                <p className="rounded-sm border border-amber-400 bg-amber-50 dark:bg-amber-950/40 p-2 text-xs"
                  data-testid="reject-approved-warning">
                  Attenzione: questa richiesta era già approvata. Passandola a <b>Rifiutata</b> le causali
                  già registrate nel calendario presenze <b>restano</b>: se non servono più, correggile
                  dal Calendario Presenze o dall'Appello.
                </p>
              )}
              <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Motivazione (facoltativa)
              </Label>
              <Input value={reject.reason} data-testid="reject-reason"
                onChange={(e) => setReject({ ...reject, reason: e.target.value })}
                placeholder="Puoi lasciare vuoto" className="h-9 rounded-sm" />
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" className="rounded-sm" onClick={() => setReject(null)} data-testid="reject-cancel">Annulla</Button>
              <Button className="rounded-sm font-semibold" data-testid="reject-confirm"
                onClick={() => decide(reject.r, "reject", reject.reason)}>Rifiuta richiesta</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {confirmApprove && (
        <Dialog open onOpenChange={() => setConfirmApprove(null)}>
          <DialogContent className="rounded-sm sm:max-w-md" data-testid="overlap-dialog">
            <DialogHeader>
              <DialogTitle className="font-head flex items-center gap-2">
                <Warning size={18} className="text-amber-500" /> Ferie sovrapposte
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                {TYPE_LABELS[confirmApprove.type]} di{" "}
                <span className="font-semibold text-foreground">{confirmApprove.employee_name}</span>{" "}
                dal {itDate(confirmApprove.date_from)} al {itDate(confirmApprove.date_to)}.
                Nello stesso periodo risultano già in ferie:
              </p>
              <div className="rounded-sm border border-border divide-y divide-border">
                {confirmApprove.overlaps.map((o) => (
                  <div key={o.employee_name} className="px-3 py-2 flex items-center justify-between gap-3 text-sm"
                    data-testid={`overlap-item-${o.employee_name}`}>
                    <span className="font-semibold">{o.employee_name}</span>
                    <span className="text-xs text-muted-foreground tabular-nums whitespace-nowrap">
                      {itDate(o.date_from)} → {itDate(o.date_to)} · {o.days} gg
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" className="rounded-sm" data-testid="overlap-cancel"
                onClick={() => setConfirmApprove(null)}>Annulla</Button>
              <Button className="rounded-sm font-semibold" data-testid="overlap-approve"
                onClick={() => decide(confirmApprove, "approve")}>Approva comunque</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {approve && (
        <Dialog open onOpenChange={() => setApprove(null)}>
          <DialogContent className="rounded-sm sm:max-w-md" data-testid="approve-dialog">
            <DialogHeader>
              <DialogTitle className="font-head">Causale per il registro giornaliero</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                {TYPE_LABELS[approve.r.type]} di{" "}
                <span className="font-semibold text-foreground">{approve.r.employee_name}</span>{" "}
                dal {itDate(approve.r.date_from)} al {itDate(approve.r.date_to)} ({approve.r.days}{" "}
                {approve.r.days === 1 ? "giorno" : "giorni"}).
                Scegli la causale da registrare nel calendario presenze.
              </p>
              <div className="grid grid-cols-3 gap-2">
                {Object.keys(STATUS_META).map((k) => (
                  <button key={k} type="button" data-testid={`approve-status-${k}`}
                    onClick={() => setApprove({ ...approve, status: k })}
                    className={`rounded-sm border p-2 text-left transition-colors duration-150 ${
                      approve.status === k ? "border-primary bg-secondary" : "border-border hover:bg-secondary/50"}`}>
                    <span className={`px-1.5 py-0.5 rounded-sm text-[11px] font-bold ${STATUS_META[k].cell}`}>
                      {STATUS_META[k].short}
                    </span>
                    <div className="text-[11px] font-semibold mt-1">{k}</div>
                  </button>
                ))}
              </div>
              {approve.r.overlaps?.length > 0 && (
                <div className="rounded-sm border border-amber-400 bg-amber-50 dark:bg-amber-950/40 p-2 text-xs"
                  data-testid="approve-overlaps">
                  <div className="font-semibold text-amber-800 dark:text-amber-300 flex items-center gap-1.5">
                    <Warning size={13} /> Nello stesso periodo sono già in ferie:
                  </div>
                  {approve.r.overlaps.map((o) => (
                    <div key={o.employee_name} className="mt-1 flex justify-between gap-2">
                      <span className="font-semibold">{o.employee_name}</span>
                      <span className="tabular-nums text-muted-foreground whitespace-nowrap">
                        {itDate(o.date_from)} → {itDate(o.date_to)} · {o.days} gg
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" className="rounded-sm" data-testid="approve-cancel"
                onClick={() => setApprove(null)}>Annulla</Button>
              <Button className="rounded-sm font-semibold" data-testid="approve-confirm"
                onClick={() => decide(approve.r, "approve", null, approve.status)}>
                <Check size={14} className="mr-1.5" /> Approva e registra {STATUS_META[approve.status]?.short}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {vacationOf && (
        <VacationDialog employeeId={vacationOf.id} name={vacationOf.name}
          onClose={() => setVacationOf(null)} />
      )}
    </div>
  );
}
